import { useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import {
  StayAccess,
  StayCountLabel,
  StayGuestCell,
  StayLines,
  StayListToolbar,
  StayPitchCell,
  StaySortHeader,
  StayStat,
} from "@/components/bookings/stayList";
import {
  formatPartyLine,
  matchesStayQuery,
  nextStaySort,
  nightsLabel,
  useFilteredStayRows,
  useStayListState,
} from "@/components/bookings/stayListModel";
import { formatIsoDateShort } from "@/lib/bookingDates";
import {
  hasBookingDraftInvoice,
  bookingDraftInvoiceAmount,
} from "@/lib/bookingInvoiceStatus";
import {
  hasMissingInvoice,
  hasOpenInvoiceAmount,
  invoiceNeedsAttention,
  openInvoiceCount,
} from "@/lib/bookingInvoiceDisplay";
import { buildBookingsOpenUrl } from "@/lib/bookingNavigation";
import { resolveGuestName } from "@/lib/guestDisplay";
import { formatCurrency } from "@/lib/money";
import { formatLicensePlate } from "@/lib/vehicle";
import type { BookingDeparture, Pitch } from "@/types/camping";

function pitchCodeOf(booking: BookingDeparture, pitches: Pitch[]): string {
  if (!booking.pitch_id) return "Offen";
  return pitches.find((pitch) => pitch.id === booking.pitch_id)?.code ?? booking.pitch_id.slice(0, 8);
}

function balanceSortValue(booking: BookingDeparture): number {
  if (hasOpenInvoiceAmount(booking)) return Number(booking.open_invoice_amount) || 0;
  if (hasMissingInvoice(booking)) return 0;
  if (hasBookingDraftInvoice(booking)) return -0.5;
  return -1;
}

export interface DeparturesTableProps {
  bookings: BookingDeparture[];
  pitches: Pitch[];
  canEdit: boolean;
  canCheckOutToday: boolean;
  checkoutStrict: boolean;
  checkOutLoadingId: string | null;
  listKey: string;
  emptyMessage: string;
  onOpen: (booking: BookingDeparture) => void;
  onCheckOut: (booking: BookingDeparture) => void;
}

export function DeparturesTable({
  bookings,
  pitches,
  canEdit,
  canCheckOutToday,
  checkoutStrict,
  checkOutLoadingId,
  listKey,
  emptyMessage,
  onOpen,
  onCheckOut,
}: DeparturesTableProps) {
  const { search, setSearch, sort, setSort } = useStayListState(listKey);

  const guestName = useCallback(
    (booking: BookingDeparture) => resolveGuestName(booking.guest_id, { booking }),
    [],
  );
  const pitchCode = useCallback(
    (booking: BookingDeparture) => pitchCodeOf(booking, pitches),
    [pitches],
  );

  const matches = useCallback(
    (booking: BookingDeparture, query: string) =>
      matchesStayQuery(query, [
        guestName(booking),
        booking.booking_number,
        pitchCode(booking),
        booking.license_plate,
        booking.key_number,
        booking.transponder_id,
        booking.departure_date,
      ]),
    [guestName, pitchCode],
  );

  const sortValue = useCallback(
    (booking: BookingDeparture, key: string) => {
      switch (key) {
        case "guest":
          return guestName(booking);
        case "pitch":
          return pitchCode(booking) === "Offen" ? "\uffff" : pitchCode(booking);
        case "stay":
          return booking.departure_date;
        case "balance":
          return balanceSortValue(booking);
        default:
          return booking.booking_number;
      }
    },
    [guestName, pitchCode],
  );

  const visible = useFilteredStayRows(bookings, search, sort, matches, sortValue);
  const filtering = search.trim().length > 0 && visible.length !== bookings.length;
  const overdueCount = bookings.filter((booking) => booking.is_departure_overdue).length;
  const openCount = bookings.filter((booking) => hasOpenInvoiceAmount(booking)).length;
  const missingCount = bookings.filter((booking) => hasMissingInvoice(booking)).length;

  return (
    <Table
      className="eh-table--stay"
      isEmpty={visible.length === 0}
      emptyMessage={
        search.trim() && bookings.length > 0 ? "Keine Treffer für die Suche." : emptyMessage
      }
      toolbar={
        <StayListToolbar
          search={search}
          onSearchChange={setSearch}
          placeholder="Gast, Nr., Platz, Schlüssel…"
          searchLabel="Abreisen durchsuchen"
          stats={
            <>
              <StayCountLabel
                shown={visible.length}
                total={bookings.length}
                singular="Abreise"
                plural="Abreisen"
                filtering={filtering}
              />
              {overdueCount > 0 ? (
                <StayStat tone="danger">
                  <strong>{overdueCount}</strong>
                  überfällig
                </StayStat>
              ) : null}
              {openCount > 0 ? (
                <StayStat tone="warn">
                  <strong>{openCount}</strong>
                  offen
                </StayStat>
              ) : null}
              {missingCount > 0 ? (
                <StayStat tone="warn">
                  <strong>{missingCount}</strong>
                  ohne Rechnung
                </StayStat>
              ) : null}
            </>
          }
        />
      }
    >
      <thead>
        <tr>
          <StaySortHeader
            label="Gast"
            sortKey="guest"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <StaySortHeader
            label="Platz"
            sortKey="pitch"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <th>Zugang</th>
          <StaySortHeader
            label="Aufenthalt"
            sortKey="stay"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <StaySortHeader
            label="Rechnung"
            sortKey="balance"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          {canEdit ? (
            <th className="text-right" data-tour="departures-actions">
              Aktion
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {visible.map((booking) => {
          const overdue = Boolean(booking.is_departure_overdue);
          const open = hasOpenInvoiceAmount(booking);
          const missing = hasMissingInvoice(booking);
          const draft = hasBookingDraftInvoice(booking);
          const blocked = checkoutStrict && open;
          const plate = booking.license_plate
            ? formatLicensePlate(booking.license_plate, booking.license_plate_country)
            : null;
          const meta = [booking.booking_number, plate].filter(Boolean).join(" · ");
          const paid = (booking.invoice_count ?? 0) > 0 && !open && !missing && !draft;
          return (
            <ClickableTableRow
              key={booking.id}
              ariaLabel={`Buchung ${booking.booking_number} öffnen`}
              onActivate={() => onOpen(booking)}
              className={
                overdue
                  ? "eh-stay-row--danger"
                  : invoiceNeedsAttention(booking)
                    ? "eh-stay-row--warn"
                    : undefined
              }
            >
              <td data-label="Gast">
                <StayGuestCell
                  name={guestName(booking)}
                  meta={meta}
                  badges={
                    overdue ? (
                      <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[0.65rem] font-semibold text-status-red">
                        Überfällig
                      </span>
                    ) : null
                  }
                />
              </td>
              <td data-label="Platz">
                <StayPitchCell code={pitchCode(booking)} booking={booking} />
              </td>
              <td data-label="Zugang">
                <StayAccess keyNumber={booking.key_number} transponderId={booking.transponder_id} />
              </td>
              <td data-label="Aufenthalt">
                <StayLines
                  primary={`${overdue ? "Abreise war " : "Abreise "}${formatIsoDateShort(booking.departure_date)}`}
                  secondary={`Anreise ${formatIsoDateShort(booking.arrival_date)} · ${nightsLabel(booking.nights)} · ${formatPartyLine(booking)}`}
                />
              </td>
              <td data-label="Rechnung">
                {open ? (
                  <Link
                    to={buildBookingsOpenUrl(booking.id, booking.status)}
                    className="eh-stay-balance eh-stay-balance--open"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(booking);
                    }}
                  >
                    <span>
                      {openInvoiceCount(booking) > 1
                        ? `${openInvoiceCount(booking)} offen`
                        : "Offen"}
                    </span>
                    {formatCurrency(booking.open_invoice_amount, booking.currency)}
                  </Link>
                ) : missing ? (
                  <span className="eh-stay-balance eh-stay-balance--missing">
                    <span>Rechnung</span>
                    Fehlt
                  </span>
                ) : draft ? (
                  <span className="eh-stay-balance eh-stay-balance--draft">
                    <span>Rechnung</span>
                    {bookingDraftInvoiceAmount(booking) > 0
                      ? formatCurrency(bookingDraftInvoiceAmount(booking), booking.currency)
                      : "Entwurf"}
                  </span>
                ) : paid ? (
                  <span className="eh-stay-balance eh-stay-balance--paid">
                    <span>Rechnung</span>
                    Bezahlt
                  </span>
                ) : (
                  <span className="text-xs text-label-tertiary">—</span>
                )}
              </td>
              {canEdit ? (
                <td className="text-right" data-label="Aktion">
                  {canCheckOutToday && booking.status === "checked_in" ? (
                    <Button
                      type="button"
                      variant={blocked ? "secondary" : "primary"}
                      size="sm"
                      loading={checkOutLoadingId === booking.id}
                      disabled={blocked}
                      title={
                        blocked
                          ? "Check-out blockiert: offene Rechnung – bitte zuerst bezahlen"
                          : open
                            ? "Gast und offene Rechnungen anzeigen"
                            : "Check-out starten"
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        onCheckOut(booking);
                      }}
                    >
                      Auschecken
                    </Button>
                  ) : (
                    <span className="text-xs text-label-tertiary">—</span>
                  )}
                </td>
              ) : null}
            </ClickableTableRow>
          );
        })}
      </tbody>
    </Table>
  );
}
